import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Play, Pause, Music, Sparkles, Link2, Video, Volume2 } from 'lucide-react'
import { getPlaylist, getAudios, getAudioPlaylist, BACKEND_URL, type Mix } from '@meno/ui/lib/api'
import PdfThumbnail from '@/components/PdfThumbnail'
import { TransitionPreview } from '@/components/TransitionPreview'
import { TRANSITIONS } from '@/lib/transitions'

type MediaFile = { id: number; original_name: string; file_type: string; url: string; duration?: number }
type LinkItem = { id: number; name: string; interactive_link?: string }
type ContentRow =
  | { key: string; kind: 'media'; item: MediaFile; duration: number; isVideo: boolean; isImage: boolean; isPdf: boolean }
  | { key: string; kind: 'link'; item: LinkItem; duration: number }
type AudioTrack = { url: string; name: string }

const DEFAULT_DURATION = 10

const resolveUrl = (url: string) => (url.startsWith('http') ? url : `${BACKEND_URL}${url}`)

// Plays the mix's linked playlist as a real sequence — the same "cycle
// through items on a timer, videos advance on end" preview PlaylistDetailsPage
// uses — with the selected audio playing alongside as its own independent
// channel (matching how audio actually runs on a real screen: a separate
// loop, not synced beat-for-beat to the visual content) and the selected
// transition shown as a small looping style badge. Layouts have no single
// sequence to play through (multiple zones at once), so this only activates
// for a playlist main content; MixDetailsPage falls back to the static
// identity card for a layout.
export default function MixSequencePreview({ mix }: { mix: Mix }) {
  const { t } = useTranslation()
  const [rows, setRows] = useState<ContentRow[]>([])
  const [loadingContent, setLoadingContent] = useState(false)
  const [audioTracks, setAudioTracks] = useState<AudioTrack[]>([])
  const [audioTrackIdx, setAudioTrackIdx] = useState(0)

  const [previewing, setPreviewing] = useState(false)
  const [idx, setIdx] = useState(0)
  const timerRef = useRef<number | null>(null)
  const audioRef = useRef<HTMLAudioElement>(null)

  // Load the linked playlist's content whenever it changes.
  useEffect(() => {
    setRows([])
    setIdx(0)
    setPreviewing(false)
    if (mix.playlist_id == null) return
    setLoadingContent(true)
    getPlaylist(mix.playlist_id)
      .then(({ data }) => {
        type WithPos = ContentRow & { pos: number }
        const mediaRows: WithPos[] = (data.media || []).map((m: MediaFile) => {
          const isImage = m.file_type.startsWith('image')
          const isVideo = m.file_type.startsWith('video')
          const isPdf = m.file_type === 'application/pdf' || /\.pdf$/i.test(m.original_name)
          const explicit = data.media_durations?.[m.id]
          return {
            key: `m-${m.id}`, kind: 'media', item: m,
            duration: explicit ?? (isVideo ? (m.duration || DEFAULT_DURATION) : DEFAULT_DURATION),
            isImage, isVideo, isPdf,
            pos: data.media_order?.[m.id] ?? 1e6,
          }
        })
        const linkRows: WithPos[] = (data.links || []).map((l: LinkItem) => ({
          key: `l-${l.id}`, kind: 'link', item: l,
          duration: data.link_durations?.[l.id] ?? DEFAULT_DURATION,
          pos: data.link_order?.[l.id] ?? 1e6,
        }))
        setRows([...mediaRows, ...linkRows].sort((a, b) => a.pos - b.pos))
      })
      .catch(() => {})
      .finally(() => setLoadingContent(false))
  }, [mix.playlist_id])

  // Load the selected audio's playable track(s) — a single file, or the
  // ordered tracks of an audio playlist.
  useEffect(() => {
    setAudioTracks([])
    setAudioTrackIdx(0)
    if (mix.audio_id != null) {
      getAudios().then(({ data }) => {
        const a = (data || []).find((x: MediaFile) => x.id === mix.audio_id)
        if (a) setAudioTracks([{ url: resolveUrl(a.url), name: a.original_name }])
      }).catch(() => {})
    } else if (mix.audio_playlist_id != null) {
      getAudioPlaylist(mix.audio_playlist_id).then(({ data }) => {
        const tracks = (data.tracks || [])
          .slice()
          .sort((a: { position: number }, b: { position: number }) => a.position - b.position)
          .map((tr: { url: string; original_name: string }) => ({ url: resolveUrl(tr.url), name: tr.original_name }))
        setAudioTracks(tracks)
      }).catch(() => {})
    }
  }, [mix.audio_id, mix.audio_playlist_id])

  const stop = () => {
    if (timerRef.current) { window.clearTimeout(timerRef.current); timerRef.current = null }
    setPreviewing(false)
    setIdx(0)
    audioRef.current?.pause()
  }

  const advance = (from: number) => {
    if (from + 1 >= rows.length) { stop(); return }
    setIdx(from + 1)
  }

  const toggle = () => {
    if (previewing) { stop(); return }
    if (rows.length === 0) return
    setIdx(0)
    setPreviewing(true)
    audioRef.current?.play().catch(() => {})
  }

  // Advance the visible item on a timer (videos advance via onEnded instead).
  useEffect(() => {
    if (!previewing) return
    const row = rows[idx]
    if (!row) { stop(); return }
    if (row.kind === 'media' && row.isVideo) return
    const ms = Math.max(1, row.duration) * 1000
    timerRef.current = window.setTimeout(() => advance(idx), ms)
    return () => { if (timerRef.current) { window.clearTimeout(timerRef.current); timerRef.current = null } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewing, idx, rows.length])

  const row = rows[idx]
  const transitionName = mix.transition ? (TRANSITIONS.find(tr => tr.key === mix.transition)?.name ?? mix.transition) : null
  const audioLabel = audioTracks[audioTrackIdx]?.name ?? null

  if (loadingContent) {
    return <div className="h-40 flex items-center justify-center text-xs text-muted-foreground">{t('common.loading', 'Loading…')}</div>
  }
  if (rows.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-10 text-center">
        {t('mix.noSequence', 'Pick a playlist as the main content to preview its sequence here.')}
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <div className="relative aspect-video w-full rounded-xl border-2 border-border bg-muted/30 overflow-hidden flex items-center justify-center">
        {row?.kind === 'media' && row.isImage && (
          <img src={resolveUrl(row.item.url)} alt={row.item.original_name} className="w-full h-full object-contain" />
        )}
        {row?.kind === 'media' && row.isVideo && (
          previewing ? (
            <video
              key={row.key}
              src={resolveUrl(row.item.url)}
              autoPlay
              muted
              playsInline
              onEnded={() => advance(idx)}
              className="w-full h-full object-contain"
            />
          ) : (
            <Video className="w-12 h-12 text-muted-foreground/60" />
          )
        )}
        {row?.kind === 'media' && row.isPdf && (
          <PdfThumbnail url={resolveUrl(row.item.url)} targetWidth={480} fit="contain" className="w-full h-full" />
        )}
        {row?.kind === 'link' && (
          <div className="flex flex-col items-center gap-2 text-center px-6">
            <Link2 className="w-10 h-10 text-teal-600" />
            <p className="text-sm font-medium">{row.item.name}</p>
          </div>
        )}

        {previewing && (
          <div className="absolute top-2 end-2 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-black/55 text-white backdrop-blur-sm">
            {idx + 1} / {rows.length}
          </div>
        )}

        {/* Transition + audio badges share one flex row (not two independently
            absolute-positioned elements) so they split the available width
            and truncate instead of overlapping on a narrow phone frame. */}
        {(transitionName || audioLabel) && (
          <div className="absolute bottom-2 start-2 end-2 flex items-center justify-between gap-2">
            {/* Transition style badge — a small looping demo of the mix's
                transition, not a real cut applied between these frames
                (that's the players' job on a real screen); just shows what
                it looks like. */}
            {transitionName ? (
              <div className="min-w-0 flex items-center gap-1.5 rounded-md bg-black/55 backdrop-blur-sm ps-1.5 pe-2 py-1 shrink">
                <div className={`w-6 h-5 rounded overflow-hidden shrink-0 ${previewing ? 'tr-playing' : ''}`}>
                  <TransitionPreview anim={`tr-${mix.transition}`} className="w-full h-full" logoClass="h-2.5" />
                </div>
                <span className="text-[10px] font-medium text-white flex items-center gap-1 min-w-0">
                  <Sparkles className="w-2.5 h-2.5 shrink-0" />
                  <span className="truncate">{transitionName}</span>
                </span>
              </div>
            ) : <span />}

            {audioLabel && (
              <div className="min-w-0 flex items-center gap-1 text-[10px] font-medium px-1.5 py-1 rounded bg-black/55 text-white backdrop-blur-sm shrink">
                <Volume2 className="w-2.5 h-2.5 shrink-0" />
                <span className="truncate">{audioLabel}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <PreviewPlayButton onClick={toggle} rows={rows} previewing={previewing} />
        {mix.audio_id != null || mix.audio_playlist_id != null ? (
          <span className="text-[11px] text-muted-foreground flex items-center gap-1 min-w-0">
            <Music className="w-3 h-3 shrink-0" />
            <span className="truncate">{t('mix.audioPlaysAlongside', 'Audio plays independently, like on a real screen')}</span>
          </span>
        ) : null}
      </div>

      {audioTracks.length > 0 && (
        <audio
          ref={audioRef}
          src={audioTracks[audioTrackIdx]?.url}
          onEnded={() => setAudioTrackIdx(i => (i + 1) % audioTracks.length)}
        />
      )}
    </div>
  )
}

function PreviewPlayButton({ onClick, rows, previewing }: { onClick: () => void; rows: ContentRow[]; previewing: boolean }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      disabled={rows.length === 0}
      onClick={onClick}
      className="gap-2 h-10 px-4 rounded-md bg-muted/40 text-muted-foreground hover:bg-muted disabled:opacity-50 inline-flex items-center transition-colors"
    >
      {previewing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      {previewing ? t('playlist.stopPreview', 'Stop Preview') : t('playlist.playPreview', 'Play Preview')}
    </button>
  )
}
